import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CornerDownLeft, Package, Sparkles } from 'lucide-react';

import {
    Command,
    CommandDialog,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    CommandShortcut,
} from '@/components/ui/command';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';
import { useAskAi } from '../assistant/AskAiContext';
import { DESTINATIONS, PaletteProduct, matchScore, productRoute } from './command-palette';

/**
 * Anything can ask for the palette — the sidebar's search field does.
 * An event rather than a context: the palette is mounted once, at the root,
 * and nothing else needs to know where.
 */
export const OPEN_PALETTE_EVENT = 'ardealy:open-palette';
export const openCommandPalette = () => window.dispatchEvent(new CustomEvent(OPEN_PALETTE_EVENT));

/** Ctrl+K on Windows and Linux, ⌘K on a Mac. */
const isPaletteChord = (e: KeyboardEvent): boolean =>
    (e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'k';

const MAX_PRODUCTS = 6;
/** Long enough to skip the requests for words still being typed. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Search, navigate, ask and act from one keystroke.
 *
 * Mounted once beside the assistant panel. It knows the app's destinations,
 * searches the vendor's products on the server as they type, and how
 * to hand a sentence to Ask AI — so whatever is typed, the last row always
 * does something.
 */
export const CommandPalette: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const productsApi = useProductsApi();
    const askAi = useAskAi();

    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [products, setProducts] = useState<PaletteProduct[]>([]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (isPaletteChord(e)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        const onOpen = () => setOpen(true);
        window.addEventListener('keydown', onKey);
        window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
        return () => {
            window.removeEventListener('keydown', onKey);
            window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
        };
    }, []);

    /*
     * Products come from the server's catalogue search — the same one the
     * catalogue uses, so a SKU, a full name or "blue rug" find the same things
     * in both. It used to download the whole catalogue on first open, which a
     * large vendor pays for in time and a 1000-product cap silently cut short.
     *
     * Asked as the vendor pauses rather than on Enter: Enter here picks a row.
     */
    const requestId = useRef(0);
    useEffect(() => {
        const text = query.trim();
        const id = ++requestId.current;
        if (!open || !text) {
            setProducts([]);
            return;
        }
        const timer = window.setTimeout(async () => {
            try {
                const { products: found } = await productsApi.findProductsPage({
                    page: 1,
                    limit: MAX_PRODUCTS,
                    draft: 'all',
                    search: text,
                });
                if (id !== requestId.current) return;
                setProducts(
                    found
                        .filter((p: any) => p?.uuid && p?.name)
                        .map((p: any) => ({ uuid: p.uuid, name: p.name, sku: p.sku })),
                );
            } catch {
                // Products simply do not appear; destinations and Ask AI still work.
                if (id === requestId.current) setProducts([]);
            }
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, query]);

    useEffect(() => {
        if (open) setQuery('');
    }, [open]);

    /**
     * Ranked here rather than by cmdk's own matcher, so the ordering rules
     * stay in one tested place — and so `shouldFilter={false}` can hand the
     * list over verbatim.
     */
    const destinations = useMemo(
        () =>
            DESTINATIONS.map((d) => ({ ...d, label: t(d.labelKey) as string, section: t(d.sectionKey) as string }))
                .map((d) => ({ ...d, score: matchScore(query, d.label, ...d.keywords) }))
                .filter((d) => d.score > 0)
                .sort((a, b) => b.score - a.score),
        [query, t],
    );

    // The server already decided what matches; rank what came back so the
    // closest name or SKU leads.
    const hits = useMemo(() => {
        if (!query.trim()) return [];
        return products
            .map((p) => ({ ...p, score: matchScore(query, p.name, p.sku) }))
            .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
            .slice(0, MAX_PRODUCTS);
    }, [query, products]);

    const go = useCallback(
        (route: string) => {
            setOpen(false);
            navigate(route);
        },
        [navigate],
    );

    return (
        <CommandDialog
            open={open}
            onOpenChange={setOpen}
            title={t('PALETTE.PLACEHOLDER') as string}
            description={t('PALETTE.ASK_HINT') as string}
        >
            {/*
              This CommandDialog renders the dialog shell only, so the cmdk
              root is ours to provide — and with it `shouldFilter={false}`,
              because the ranking lives in command-palette.ts where it is tested.
            */}
            <Command shouldFilter={false}>
            <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder={t('PALETTE.PLACEHOLDER') as string}
            />
            <CommandList>
                <CommandEmpty>{t('PALETTE.EMPTY')}</CommandEmpty>

                {destinations.length > 0 && (
                    <CommandGroup heading={t('PALETTE.KIND.GO') as string}>
                        {destinations.map((d) => {
                            const Icon = d.icon;
                            return (
                                <CommandItem key={d.route} value={d.route} onSelect={() => go(d.route)}>
                                    {Icon ? <Icon className="size-4" /> : null}
                                    <span>{d.label}</span>
                                    <CommandShortcut>{d.section}</CommandShortcut>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                )}

                {hits.length > 0 && (
                    <CommandGroup heading={t('PALETTE.KIND.PRODUCT') as string}>
                        {hits.map((p) => (
                            <CommandItem key={p.uuid} value={`product-${p.uuid}`} onSelect={() => go(productRoute(p.uuid))}>
                                <Package className="size-4" />
                                <span>{p.name}</span>
                                {p.sku && <CommandShortcut>{p.sku}</CommandShortcut>}
                            </CommandItem>
                        ))}
                    </CommandGroup>
                )}

                {/* Always last, so a query that matches nothing still does something. */}
                {query.trim() && askAi && (
                    <CommandGroup heading={t('PALETTE.KIND.ASK') as string}>
                        <CommandItem
                            value="ask-ai"
                            onSelect={() => {
                                setOpen(false);
                                askAi.open(query.trim());
                            }}
                        >
                            <Sparkles className="size-4" />
                            <span>{t('PALETTE.ASK', { query: query.trim() })}</span>
                            <CommandShortcut>
                                <CornerDownLeft className="size-3" />
                            </CommandShortcut>
                        </CommandItem>
                    </CommandGroup>
                )}
            </CommandList>
            </Command>
        </CommandDialog>
    );
};

export default CommandPalette;
